import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Image, Trash2 } from 'lucide-react';

// Same helper jaisa AdminCategory.jsx mein hai (adminToken automatically bhejta hai)
const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('adminToken');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(url, { ...options, headers });
  const raw = await response.text();

  const jsonStart = raw.search(/[{[]/);
  if (jsonStart === -1) {
    throw new Error(`Server returned an unexpected response (status ${response.status}).`);
  }

  let data;
  try {
    data = JSON.parse(raw.slice(jsonStart));
  } catch (e) {
    throw new Error('Invalid JSON response from server.');
  }

  if (!response.ok) {
    throw new Error(data.message || `Request failed: ${response.status}`);
  }

  return data;
};

const OPTION_TYPES = [
  { value: 'neck', label: 'Neck Design' },
  { value: 'sleeve', label: 'Sleeve Style' },
  { value: 'trouser', label: 'Trouser Style' },
  { value: 'cloth', label: 'Cloth Style' },
  { value: 'embellishment', label: 'Embellishment' },
];

const CustomizationOptionsModal = ({ baseUrl, subcategoryId, subcategoryName, onClose, showSweetAlert }) => {
  const imgRef = useRef(null);

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [optionType, setOptionType] = useState('neck');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`${baseUrl}/admin/customization-options/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mlsubcategory_id: subcategoryId }),
      });
      if (data.success) setOptions(data.data);
    } catch (err) {
      showSweetAlert(err.message || 'Failed to load options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcategoryId]);

  const handleImg = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showSweetAlert('Please select a valid image file!');
      return;
    }
    setImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAdd = async () => {
    if (!name.trim()) {
      showSweetAlert('Option ka naam likhna zaroori hai');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('mlsubcategory_id', subcategoryId);
      formData.append('option_type', optionType);
      formData.append('name', name.trim());
      if (price) formData.append('price', price);
      if (image) formData.append('image', image);

      const data = await apiFetch(`${baseUrl}/admin/customization-options/add`, {
        method: 'POST',
        body: formData,
      });

      if (data.success) {
        showSweetAlert('Option added successfully!');
        setName('');
        setPrice('');
        setImage(null);
        setImagePreview(null);
        fetchOptions();
      } else {
        showSweetAlert(data.message || 'Failed to add option');
      }
    } catch (err) {
      showSweetAlert(err.message || 'Failed to add option');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const data = await apiFetch(`${baseUrl}/admin/customization-options/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      if (data.success) {
        setOptions((prev) => prev.filter((o) => o.id !== id));
        showSweetAlert('Option deleted');
      }
    } catch (err) {
      showSweetAlert(err.message || 'Failed to delete option');
    }
  };

  return (
    <div className="AdminCat-modal-overlay" onClick={onClose}>
      <div className="AdminCat-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="AdminCat-modal-header">
          <h3 className="AdminCat-modal-title">Customization Options — {subcategoryName}</h3>
          <button className="AdminCat-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Add new option row */}
        <div className="AdminCat-modal-img-area" onClick={() => imgRef.current?.click()} style={{ height: 100 }}>
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="option"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <>
              <Image size={24} color="#8b5cf6" />
              <p className="AdminCat-modal-img-hint">Click to upload option image (optional)</p>
            </>
          )}
          <input
            ref={imgRef}
            type="file"
            accept="image/*"
            onChange={handleImg}
            style={{ display: 'none' }}
          />
        </div>

        <div className="AdminCat-form-group" style={{ marginTop: '1rem' }}>
          <label className="AdminCat-label">Option Type</label>
          <select
            className="AdminCat-input"
            value={optionType}
            onChange={(e) => setOptionType(e.target.value)}
          >
            {OPTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="AdminCat-form-group">
          <label className="AdminCat-label">
            Option Name <span className="AdminCat-star">*</span>
          </label>
          <input
            className="AdminCat-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Sleeveless, Pearl Trim Lace"
          />
        </div>

        <div className="AdminCat-form-group">
          <label className="AdminCat-label">Price (optional)</label>
          <input
            className="AdminCat-input"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="e.g., 850"
          />
        </div>

        <div className="AdminCat-modal-actions">
          <button className="AdminCat-cancel-btn" onClick={onClose} disabled={saving}>Close</button>
          <button className="AdminCat-save-btn" onClick={handleAdd} disabled={saving}>
            <Plus size={15} /> {saving ? 'Adding...' : 'Add Option'}
          </button>
        </div>

        {/* Existing options list */}
        <div style={{ marginTop: '1.25rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
          <p className="AdminCat-section-title" style={{ marginBottom: '0.5rem' }}>
            Existing Options ({options.length})
          </p>

          {loading ? (
            <p style={{ color: '#888' }}>Loading...</p>
          ) : options.length === 0 ? (
            <p style={{ color: '#888' }}>Abhi koi option add nahi hui.</p>
          ) : (
            <div className="AdminCat-subcat-cards-list">
              {options.map((opt) => (
                <div key={opt.id} className="AdminCat-subcat-card">
                  <div className="AdminCat-subcat-card-img">
                    {opt.image ? (
                      <img
                        src={`${baseUrl.replace('/api', '')}/storage/${opt.image}`}
                        alt={opt.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: '1.2rem' }}>🖼️</span>
                    )}
                  </div>
                  <div className="AdminCat-subcat-card-info">
                    <p className="AdminCat-subcat-card-name">{opt.name}</p>
                    <p className="AdminCat-subcat-card-products">
                      {opt.option_type}{opt.price ? ` · PKR ${opt.price}` : ''}
                    </p>
                  </div>
                  <button
                    className="AdminCat-subcat-card-delete"
                    onClick={() => handleDelete(opt.id)}
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomizationOptionsModal;